/**
 * Small shared pieces — the ones that would otherwise be re-typed on six pages
 * and drift apart by the third.
 */
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

export function StatusPill({ status }) {
  const complete = status === 'Completed';
  return (
    <span className={`status ${complete ? 'status--complete' : 'status--prayer'}`}>
      <Icon name={complete ? 'checkCircle' : 'flame'} size={13} solid={!complete} />
      {status}
    </span>
  );
}

export function Notice({ tone = 'info', icon, title, children }) {
  const fallbackIcon = { info: 'info', warn: 'alert', success: 'checkCircle', danger: 'alert' }[tone];
  return (
    <div className={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon name={icon || fallbackIcon} size={18} />
      <div>
        {title && <strong>{title}</strong>}
        <span>{children}</span>
      </div>
    </div>
  );
}

export function EmptyState({ icon = 'compass', title, children, action }) {
  return (
    <div className="empty">
      <Icon name={icon} size={48} />
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** Warm-grey placeholders — the same material as the page, not a cold patch. */
export function CardSkeleton() {
  return (
    <div className="skeleton-card" aria-hidden="true">
      <div className="skeleton skeleton-card__media" style={{ borderRadius: 0 }} />
      <div className="skeleton-card__body">
        <div className="skeleton" style={{ height: 14, width: '45%' }} />
        <div className="skeleton" style={{ height: 18, width: '75%' }} />
        <div className="skeleton" style={{ height: 12, width: '60%' }} />
        <div className="skeleton" style={{ height: 12, width: '85%' }} />
      </div>
    </div>
  );
}

export function CardGridSkeleton({ count = 6 }) {
  return (
    <div className="grid-cards">
      {Array.from({ length: count }, (_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  );
}

/**
 * Stands in for MonasteryCard specifically — block-for-block the same shape
 * (photo, Tibetan line, name, badge, location, tag row, footer), so the grid
 * does not jump when real cards replace it.
 */
export function MonasteryCardSkeleton() {
  const { t } = useTranslation();
  const Block = ({ className }) => <div className={`m-card-skeleton__block ${className}`} />;
  return (
    <div className="m-card-skeleton" role="progressbar" aria-label={t('common.loadingMonasteries', 'Loading monasteries')}>
      <Block className="m-card-skeleton__media" />
      <div className="m-card-skeleton__body">
        <Block className="m-card-skeleton__tibetan" />
        <Block className="m-card-skeleton__name" />
        <Block className="m-card-skeleton__badge" />
        <Block className="m-card-skeleton__location" />
        <div className="m-card-skeleton__tag-row">
          <Block className="m-card-skeleton__tag-wide" />
          <Block className="m-card-skeleton__tag-narrow" />
        </div>
        <Block className="m-card-skeleton__tag-wide" />
        <div className="m-card-skeleton__footer">
          <Block className="m-card-skeleton__cta" />
        </div>
      </div>
    </div>
  );
}

export function MonasteryGridSkeleton({ count = 6 }) {
  return (
    <div className="grid-cards">
      {Array.from({ length: count }, (_, index) => (
        <MonasteryCardSkeleton key={index} />
      ))}
    </div>
  );
}

/** The section heading pattern: icon plate, then title. */
export function CardHeading({ icon, title, children }) {
  return (
    <div className="card-heading">
      <span className="card-heading__plate">
        <Icon name={icon} size={17} />
      </span>
      <h2>{title}</h2>
      {children}
    </div>
  );
}

export function Field({ label, hint, error, htmlFor, children, optional }) {
  const { t } = useTranslation();
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {optional && <span className="subtle" style={{ fontWeight: 500 }}> · {t('common.optional', 'optional')}</span>}
      </label>
      {hint && <p className="field__hint">{hint}</p>}
      {children}
      {error && (
        <p className="field__error" role="alert">
          <Icon name="alert" size={14} /> {error}
        </p>
      )}
    </div>
  );
}

/**
 * The step trail through the offering flow. Shows where the donor is without
 * offering navigation — you cannot jump to checkout before choosing a ritual,
 * so the steps are not links.
 */
export function StepsTrail({ current }) {
  const { t } = useTranslation();
  const steps = [
    t('common.stepsTrail.step1', 'Choose the offering'),
    t('common.stepsTrail.step2', 'Write the dedication'),
    t('common.stepsTrail.step3', 'Confirm'),
  ];
  return (
    <ol className="steps-trail">
      {steps.map((label, index) => (
        <li
          key={index}
          className={`steps-trail__step${
            index === current ? ' is-current' : index < current ? ' is-done' : ''
          }`}
        >
          <span className="steps-trail__dot" />
          {label}
          {index < steps.length - 1 && <Icon name="chevronRight" size={13} style={{ opacity: 0.5 }} />}
        </li>
      ))}
    </ol>
  );
}

export function OfferingsOpeningNotice() {
  const { t } = useTranslation();
  return (
    <Notice
      tone="info"
      icon="lotus"
      title={t('common.openingNotice.title', 'Before you confirm')}
    >
      {t(
        'common.openingNotice.body',
        'We hold your offering exactly as you have written it and confirm with you before it is placed with the monastery. It is fully refundable until the ritual begins.'
      )}
    </Notice>
  );
}
